import { Typography } from 'src/components/new/Typography';
import { JobStatusHistory, JobStatusType } from '../../../../pages/dashboard/util';
import CancelButton from 'src/pages/dashboard/components/CancelButton';
import { useEffect, useRef, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import JobStatus from 'src/pages/admin/jobs/reports-components/JobStatus';
import OnHoldReasonBadge, { OnHoldReasonType } from 'src/pages/admin/jobs/reports-components/OnHoldReasonBadge';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store/store';

interface OverviewSectionProps {
  items: { label: string; value: string | number | null }[];
  jobStatus: JobStatusType;
  jobId: number;
  statusHistory: JobStatusHistory[];
  showCancelButton: boolean;
  isCosAdmin: boolean;
  isCosMember: boolean;
  onHoldReason?: OnHoldReasonType | null;
  onJobCancel: () => void;
  onStatusChange: (info: { id: number; status: JobStatusType }) => void;
  /**
   * True when the job side-panel has unsaved document edits. When set, the
   * "In Queue" option in the status dropdown is disabled, since admins may
   * only move a job to In Queue once all document changes are saved.
   */
  hasUnsavedDocumentChanges?: boolean;
}

const STATUSES = [
  'in_queue',
  'processing',
  'printing',
  'completed',
  'completed_payment_declined',
  'on_hold',
] as JobStatusType[];

const getAvailableStatuses = (currentStatus: JobStatusType, priorStatus: JobStatusType): JobStatusType[] => {
  if (currentStatus === 'printing') {
    // Hold is only valid from in_queue; printing jobs cannot be held manually.
    return ['completed'];
  }
  if (currentStatus === 'in_queue') {
    // Only in_queue jobs may be placed on manual hold.
    return ['on_hold'];
  }
  if (currentStatus !== 'on_hold' && currentStatus !== 'on_hold_manual') {
    // Processing, completed, cancelled — no valid manual transitions.
    return [];
  }
  return priorStatus === 'completed' ? ['completed'] : [priorStatus, 'in_queue', 'processing'];
};

const OverviewSection = ({
  items,
  jobStatus,
  showCancelButton,
  jobId,
  statusHistory,
  onJobCancel,
  isCosAdmin,
  isCosMember,
  onHoldReason,
  onStatusChange,
  hasUnsavedDocumentChanges = false,
}: OverviewSectionProps) => {
  const [showStatusOptions, setShowStatusOptions] = useState(false);

  const popupRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const isChangingJobStatus = useSelector((state: RootState) => state.admin.isChangingJobStatus);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (buttonRef.current?.contains(event.target)) return;

      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShowStatusOptions(false);
      }
    };

    if (showStatusOptions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatusOptions]);

  // Guard against short histories: a freshly-created/draft job may have fewer
  // than 2 status entries, so statusHistory[length - 2] would be undefined and
  // reading .status off it crashes the whole detail view. Fall back to the
  // current status when there's no prior entry.
  const priorStatus = statusHistory[statusHistory.length - 2]?.status ?? jobStatus;
  const availableStatuses = getAvailableStatuses(jobStatus, priorStatus);

  const handleStatusChange = (status: JobStatusType) => {
    // Guard: admins may only move a job to In Queue or Processing once all
    // document changes in the side panel are saved.
    if ((status === 'in_queue' || status === 'processing') && hasUnsavedDocumentChanges) return;
    onStatusChange({ status, id: jobId });
    setShowStatusOptions(false);
  };

  // An option is unavailable when the workflow doesn't allow it, or — for the
  // In Queue and Processing transitions specifically — when there are unsaved
  // document changes.
  const isStatusDisabled = (status: JobStatusType) => {
    if (!availableStatuses.includes(status)) return true;
    if ((status === 'in_queue' || status === 'processing') && hasUnsavedDocumentChanges) return true;
    return false;
  };

  const onJobStatusClick = () => {
    setShowStatusOptions((prev) => !prev);
  };

  const customArrowToShow =
    isCosAdmin && jobStatus !== 'cancelled' ? showStatusOptions ? <ArrowDropUpIcon /> : <ArrowDropDownIcon /> : null;

  return (
    <>
      <Typography
        sx={{
          marginTop: '0.9375rem',
          marginBottom: '0.9375rem',
          fontWeight: '600',
        }}
      >
        Overview
      </Typography>
      <div
        style={{
          padding: '0.9375rem',
          border: '0.0625rem solid #cac4d0',
          borderRadius: '0rem',
        }}
      >
        <div style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '1.625rem' }}>
          <div ref={buttonRef} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <JobStatus
              onClick={isCosAdmin && jobStatus !== 'cancelled' ? onJobStatusClick : () => { }}
              width="9.375rem"
              sx={{ cursor: isCosAdmin && jobStatus !== 'cancelled' ? 'pointer' : 'auto' }}
              disabled={isChangingJobStatus}
              endAdornment={customArrowToShow}
              status={jobStatus}
            />
            {jobStatus === 'in_queue' && (
              <Tooltip
                title={
                  <Box sx={{ p: '4px 2px' }}>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, mb: '6px', color: 'inherit' }}>
                      Edits can only be made to your job while it is In Queue:
                    </Typography>
                    <Typography sx={{ fontSize: '0.88rem', color: 'inherit' }}>• Same-day: Up to 15 minutes post-submission</Typography>
                    <Typography sx={{ fontSize: '0.88rem', color: 'inherit' }}>• Next business day: 12:10am PT</Typography>
                    <Typography sx={{ fontSize: '0.88rem', mt: '6px', fontStyle: 'italic', color: 'inherit' }}>
                      Please call or chat our team immediately if changes need to be made.
                    </Typography>
                  </Box>
                }
                placement="right"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: '#424242',
                      maxWidth: '280px',
                      borderRadius: '6px',
                      p: '10px 14px',
                    },
                  },
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: '1.4rem', color: 'rgba(69, 70, 79, 0.50)', cursor: 'pointer' }} />
              </Tooltip>
            )}
          </div>
          {isCosMember && onHoldReason && <OnHoldReasonBadge reason={onHoldReason} />}
          {showCancelButton && (
            <CancelButton customText="Cancel Job" onClick={onJobCancel} disabled={isChangingJobStatus} color="#ac3737" />
          )}
          {showStatusOptions && (
            <Box
              ref={popupRef}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                paddingY: '1.25rem',
                paddingX: '2.5rem',
                position: 'absolute',
                backgroundColor: 'white',
                mt: 40,
                boxShadow: '-0.25rem 0.6875rem 1.375rem 0rem rgba(0,0,0,0.75)',
                animation: 'fadeInScale 0.3s ease-out forwards',
              }}
            >
              {STATUSES.map((status) => {
                const disabled = isStatusDisabled(status);
                const gatedByUnsavedChanges =
                  (status === 'in_queue' || status === 'processing') && hasUnsavedDocumentChanges && availableStatuses.includes(status);

                const option = (
                  <JobStatus
                    key={status}
                    sx={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                    width="15.625rem"
                    disabled={disabled}
                    onClick={() => handleStatusChange(status)}
                    status={status as JobStatusType}
                  />
                );

                if (gatedByUnsavedChanges) {
                  return (
                    <Tooltip
                      key={status}
                      title="Please save all document changes before moving to Processing."
                      placement="left"
                      arrow
                    >
                      {/* span wrapper so the tooltip works on a disabled element */}
                      <span style={{ display: 'inline-flex' }}>{option}</span>
                    </Tooltip>
                  );
                }

                return option;
              })}
            </Box>
          )}
        </div>

        {items.map((section) =>
          section.value !== null && section.value !== undefined ? (
            <Typography key={section.label} sx={{ marginBottom: '0.75rem' }}>
              <span style={{ fontWeight: '500' }}>{section.label}: </span>
              {section.value}
            </Typography>
          ) : null,
        )}
      </div>
    </>
  );
};

export default OverviewSection;
